/**
 * Sonner-backed toast system. Use `toast()` from `@ezhire/ui` to fire.
 *
 * Durations per spec: 4s info/success, 8s warning, persist errors.
 * Max 3 visible at once; Sonner queues the rest.
 */

import { Toaster as SonnerToaster, toast } from 'sonner';
import { cn } from '../lib/cn';

export interface AtlasToasterProps {
  theme?: 'light' | 'dark' | 'system';
  className?: string;
}

export function Toaster({ theme = 'system', className }: AtlasToasterProps) {
  return (
    <>
      {/* Override Sonner's default close-button position (top-left) to top-right,
          and colour it with the danger token so it's clearly dismissible. */}
      <style>{`
        [data-sonner-toaster] [data-close-button] {
          left: auto !important;
          right: -8px !important;
          top: -8px !important;
          background-color: var(--danger) !important;
          border-color: var(--danger) !important;
          color: #ffffff !important;
        }
        [data-sonner-toaster] [data-close-button]:hover {
          opacity: 0.85;
        }
      `}</style>
      <SonnerToaster
        position="top-right"
        theme={theme}
        visibleToasts={3}
        duration={4000}
        closeButton
        className={cn(className)}
        toastOptions={{
          classNames: {
            toast: cn(
              'rounded-lg border border-border bg-bg-surface text-text-primary shadow-xl',
              'px-4 py-3 w-[360px]',
              'font-sans',
            ),
            title: 'text-body font-medium',
            description: 'text-caption text-text-secondary',
            actionButton:
              'bg-accent-500 text-white hover:bg-accent-600 rounded-md px-3 h-7 text-caption',
            cancelButton: 'bg-bg-elevated text-text-primary rounded-md px-3 h-7 text-caption',
            closeButton: 'border-0',
            success: 'border-l-4 border-l-success',
            error: 'border-l-4 border-l-danger',
            warning: 'border-l-4 border-l-warning',
            info: 'border-l-4 border-l-info',
          },
        }}
      />
    </>
  );
}

export { toast };
